import type { FeatureCollection, LineString } from "geojson";
import type { MountainMapNetwork } from "@/lib/maps/mountain-network";
import { UUID } from "./outings";
import { validInvitationRoute, type SharedInvitationRoute } from "./outing-sharing";

export type SharedMapReference = { id: string; revision: number; snapshotId: string };
export type SharedMapRoute = Omit<SharedInvitationRoute, "steps"> & {
  steps: (SharedInvitationRoute["steps"][number] & { refId: string | null })[];
};
export type SharedMapRead = SharedMapReference & { owner: string; route: SharedMapRoute };
export const SHARED_MAP_MAX_BYTES = 262_144;
export const SHARED_MAP_TTL = 30_000;
export function validSharedMapReference(value: SharedMapReference): boolean {
  return UUID.test(value.id) && UUID.test(value.snapshotId) && Number.isSafeInteger(value.revision) && value.revision > 0 && value.revision < 2_147_483_647;
}
export function sameSharedMap(a: SharedMapReference, b: SharedMapReference): boolean {
  return a.id === b.id && a.revision === b.revision && a.snapshotId === b.snapshotId;
}
export function sharedMapHref(ref: SharedMapReference): string {
  return `/companion/explore?${new URLSearchParams({ sharedOuting: ref.id, sharedVersion: String(ref.revision), sharedRoute: ref.snapshotId })}`;
}
export function validSharedMapRead(value: unknown): value is SharedMapRead {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const v = value as SharedMapRead;
  if (Object.keys(v).length !== 5 || !Object.keys(v).every(k => ["owner", "id", "revision", "snapshotId", "route"].includes(k))
    || typeof v.owner !== "string" || !UUID.test(v.owner) || !validSharedMapReference(v) || !v.route || !Array.isArray(v.route.steps)) return false;
  if (!v.route.steps.every(s => s && Object.keys(s).length === 5 && Object.keys(s).every(k => ["kind", "label", "departHour", "arriveHour", "refId"].includes(k))
    && (s.refId === null || typeof s.refId === "string" && s.refId.length > 0 && s.refId.length <= 160 && ["trail", "lift"].includes(s.kind)))) return false;
  return validInvitationRoute({ ...v.route, steps: v.route.steps.map(({ refId: _ref, ...step }) => step) });
}

/** Resolve only exact public feature IDs. Never connect missing legs or use private coordinates. */
export function sharedMapGeometry(route: SharedMapRoute | null, network: MountainMapNetwork, slug: string, sport: string): {
  lines: FeatureCollection<LineString>; unmapped: number; matched: boolean;
} {
  const lines: FeatureCollection<LineString> = { type: "FeatureCollection", features: [] };
  if (!route || route.slug !== slug || route.sport !== sport) return { lines, unmapped: 0, matched: false };
  const trails = new Map(network.routes.map(r => [r.id, r])), lifts = new Map(network.lifts.map(l => [l.id, l]));
  let unmapped = 0, coordinates = 0;
  route.steps.forEach((step, index) => {
    const geometry = step.refId ? (step.kind === "trail" ? trails : step.kind === "lift" ? lifts : null)?.get(step.refId)?.geometry : null;
    if (!geometry || geometry.coordinates.length < 2 || coordinates + geometry.coordinates.length > 50_000
      || geometry.coordinates.some(c => c.length < 2 || !Number.isFinite(c[0]) || !Number.isFinite(c[1]) || Math.abs(c[0]) > 180 || Math.abs(c[1]) > 90)) { unmapped++; return; }
    coordinates += geometry.coordinates.length;
    lines.features.push({ type: "Feature", properties: { index, kind: step.kind }, geometry });
  });
  return { lines, unmapped, matched: true };
}
